import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Table } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@shared/contexts";

import type {
  AdminComplexFilters,
  ListAdminComplexesResponseItem,
} from "../../api/complexes";
import { AdminComplexesActionMenu } from "./AdminComplexesActionMenu";
import { AdminComplexesFilter } from "./AdminComplexesFilter";
import { ComplexDetailsModal } from "./ComplexDetailsModal";

interface AdminComplexesTabProps {
  complexes: ListAdminComplexesResponseItem[];
  isLoading?: boolean;
  onFilter: (filters: Partial<AdminComplexFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<AdminComplexFilters>;
  paginatedMeta?: PaginationMeta;
}

const AdminComplexesTab = ({
  complexes,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: AdminComplexesTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);

  const [detailComplexId, setDetailComplexId] = useState<string | null>(null);

  const handleViewDetails = (complex: ListAdminComplexesResponseItem) => {
    setDetailComplexId(complex.id);
  };

  const handleCloseDetails = () => {
    setDetailComplexId(null);
  };

  const createActionMenu = (rowData: {
    rowData: ListAdminComplexesResponseItem;
  }) => {
    const complex = rowData.rowData;

    return (
      <AdminComplexesActionMenu
        complex={complex}
        onViewDetails={handleViewDetails}
      />
    );
  };

  const fallback = "—";

  return (
    <div>
      <Table<ListAdminComplexesResponseItem>
        userId={me?.id ?? ""}
        tableId="admin-complexes-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={createActionMenu as React.ComponentType}
        TableFilter={() => (
          <AdminComplexesFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate("admin.complexes.table.filterButtonText")}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "name",
            label: translate("admin.complexes.table.column.name"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "fantasyName",
            label: translate("admin.complexes.table.column.fantasyName"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "cnpj",
            label: translate("admin.complexes.table.column.cnpj"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "responsibleName",
            label: translate(
              "admin.complexes.table.column.responsibleName",
            ),
            minWidth: "150px",
            sortType: "text",
          },
        ]}
        tableData={complexes.map(
          (c) =>
            ({
              ...c,
              fantasyName: c.fantasyName ?? fallback,
              responsibleName: c.responsibleName ?? fallback,
            }) as { [key: string]: unknown; color?: string },
        )}
      />

      <ComplexDetailsModal
        open={detailComplexId !== null}
        complexId={detailComplexId}
        onClose={handleCloseDetails}
      />
    </div>
  );
};

export default AdminComplexesTab;
